import { useQuery } from '@tanstack/react-query';
import { getToken, isAdmin } from '../../utils/auth';
import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

import useQueryParams from '../../hooks/useQueryParams';
import CheckListWidget from '../common/CheckListWidget';

const { VITE_API_SERVICE } = import.meta.env;

export default function ReportPublishers() {
  const { setQueryParam, removeQueryParam, getParam } = useQueryParams();
  const token = getToken();
  const userIsAdmin = isAdmin(token);
  const location = useLocation();

  const [selectedPublishers, setSelectedPublishers] = useState(new Set());

  /**
   * Handle publisher change
   * @param {string} publisherId
   * @param {boolean} checked
   */
  function handlePublisherChange(publisherId, checked) {
    const newPublishers = new Set(selectedPublishers);
    if (checked) {
      newPublishers.add(publisherId);
    } else {
      newPublishers.delete(publisherId);
    }

    setSelectedPublishers(newPublishers);

    if (newPublishers.size > 0) {
      setQueryParam('publishers', Array.from(newPublishers).join(','));
    } else {
      removeQueryParam('publishers');
    }
  }

  /**
   * Clear all publishers
   */
  function clearAllPublishers() {
    setSelectedPublishers(new Set());
    removeQueryParam('publishers');
  }

  /**
   * Select all publishers
   */
  function selectAllPublishers() {
    if (!data?.publishers) return;
    const allPublisherIds = new Set(data.publishers.map((p) => p.id));
    setSelectedPublishers(allPublisherIds);
    setQueryParam('publishers', Array.from(allPublisherIds).join(','));
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['publishers'],
    queryFn: async function () {
      const token = getToken();
      const endpoint = userIsAdmin ? '/v1/reports/publishers' : '/v1/me';

      const response = await fetch(`${VITE_API_SERVICE}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(data.message);
        }

        throw new Error('Failed to fetch publishers');
      }

      // If admin, return the publishers directly, otherwise return the user's publishers
      return userIsAdmin ? data : { publishers: data.user.publishers };
    }
  });

  // Initialize with all publishers or URL params when data loads
  useEffect(() => {
    if (data?.publishers) {
      const publishers = getParam('publishers');
      if (publishers) {
        setSelectedPublishers(new Set(publishers.split(',').map(Number)));
      } else {
        // Select all publishers by default
        const allPublisherIds = new Set(data.publishers.map((p) => p.id));
        setSelectedPublishers(allPublisherIds);
        setQueryParam('publishers', Array.from(allPublisherIds).join(','));
      }
    }
  }, [data?.publishers, location.state?.reload]);

  // Watch for URL parameter changes
  useEffect(() => {
    const publishers = getParam('publishers');
    if (!publishers) {
      setSelectedPublishers(new Set());
    }
  }, [getParam('publishers')]);

  /**
   * Map publishers to a list of objects with id and display properties
   * @returns {Array} List of publisher objects
   */
  const mappedPublishers = useMemo(
    function () {
      return (
        data?.publishers.map((publisher) => ({
          id: publisher.id,
          display: publisher.name
        })) || []
      );
    },
    [data?.publishers]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collapse collapse-arrow shadow-sm">
        <input type="checkbox" defaultChecked />
        <div className="collapse-title font-medium">
          <h4>Publishers</h4>
        </div>
        <div className="collapse-content">
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Unable to load publishers
              </p>
              <button
                onClick={() => refetch()}
                className="btn btn-sm btn-ghost gap-2"
                aria-label="Retry loading publishers"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span className="text-sm">Retry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CheckListWidget
      title="Publishers"
      searchPlaceholder="Search publishers..."
      items={mappedPublishers}
      selectedItems={selectedPublishers}
      onItemChange={handlePublisherChange}
      onClearAll={clearAllPublishers}
      onSelectAll={selectAllPublishers}
    />
  );
}
